import Phaser from 'phaser';
import type { OpenWorldCreatureCards } from '../open-world/connection';
import { openWorldMapBodyAnimKey, openWorldMapBodyTextureKey } from './open-world-map-art';

/**
 * **Os bosses, desenhados: um corpo grande, um aviso legível, e uma morte que
 * não se confunde com nada.**
 *
 * ## O que este módulo NÃO faz
 *
 * **Não desenha as habilidades.** Elas são de `open-world-boss-strike-fx.ts`,
 * que tem a máquina, as formas e as piscinas. Até ao gate V28I.BG este ficheiro
 * carregava a carga e o feixe das duas magias PROVISÓRIAS através de
 * `CardBlastFx` e `SolarLanceFx`; as magias foram removidas com o kit de cada
 * boss, e a reutilização foi com elas — nenhum boss desenha uma carta de
 * jogador.
 *
 * E não decide nada. Não há dano, geometria nem tempo neste ficheiro: a posição
 * do feixe, o tamanho da esfera e o instante do compromisso vêm do estado
 * autoritativo, e o desenho segue-os.
 *
 * ## Cada boss usa a arte do MAPA dele (gate V28I.BI)
 *
 * `bossN` para `mapIndex = N`, resolvido por `mapCatalogEntry` — a mesma fonte
 * canónica que decide a identidade, o arquétipo, a vida e a escala. Não há aqui
 * segunda tabela de identidade visual, e não há recuo para o índice 1: um mapa
 * sem arte é recusado com o índice no texto, e a cena reporta a recusa.
 *
 * A folha é pedida **sob procura**, quando o primeiro boss daquele mapa chega à
 * vista: o `preload` corre antes de o servidor dizer o que há no quadrante,
 * exatamente como acontece com os cascos. Até ela chegar, o corpo desenha-se com
 * um marcador de lugar — nunca com a arte de outro mapa.
 *
 * O **tamanho segue a variação canónica**: `scaleBps` vem do tier daquela
 * geração, e o corpo é desenhado a essa escala. Um tier 6 é visivelmente maior
 * do que um tier 1, como nos mapas normais.
 *
 * ## O papel distingue-se por um indicador discreto
 *
 * Um anel fino à volta do corpo, e não um PNG diferente: **quente** para um
 * corpo a corpo, **frio** para um conjurador. O sprite não é modificado.
 *
 * ## Pendente de avaliação
 *
 * A aparência **não está aprovada**, e nenhuma alegação de desempenho é feita
 * aqui: nada foi medido num navegador.
 */

const TEXTURE = {
  ring: 'open-world-boss-ring',
  shard: 'open-world-boss-shard',
  /**
   * **O marcador de lugar do corpo** (gate V28I.BI).
   *
   * Um disco baço, e não a arte de outro mapa: enquanto a folha do mapa não
   * chega, o que se vê tem de ser legivelmente "ainda não carregou" em vez de
   * um boss errado.
   */
  body: 'open-world-boss-body-pending',
} as const;

const BODY_PX = 64;

const RING_PX = 128;
const SHARD_PX = 14;

/**
 * Os tetos das piscinas. Provisórios, e escolhidos para três bosses a serem
 * atacados ao mesmo tempo — não medidos.
 */
const SHARD_MAX = 72;
const RING_MAX = 16;

const SHARD_LIFE_MS = 320;
const DEATH_LIFE_MS = 900;
const RING_LIFE_MS = 420;

/** Quente para o corpo a corpo, frio para o conjurador. */
const ROLE_TINT = { melee: 0xff8a5a, caster: 0x7ad2ff } as const;
const HIT_TINT = 0xfff2c4;
const POISON_TINT = 0x7dff9b;
const BLEED_TINT = 0xff5a5a;
const DEATH_TINT = 0xffd24a;
const RESTORE_TINT = 0x9fe8ff;

/**
 * **As cores dos três desfechos de uma ultimate** (gate V28I.BG).
 *
 * O anúncio é frio e grande; a execução é branca e violenta; a interrupção é
 * verde-azulada e curta — as três leem-se de relance, e nenhuma se confunde com
 * a morte do boss, que é dourada.
 */
const ULTIMATE_WARN_TINT = 0x8ec4ff;
const ULTIMATE_KILL_TINT = 0xffffff;
const ULTIMATE_BREAK_TINT = 0x5affc4;

/** A ultimate em curso, como quem a vai sofrer a vê (gate V28I.BG). */
export interface BossDrawUltimate {
  readonly ability: 'abyssal_collapse' | 'sentence' | 'decree';
  readonly phase: 'telegraph' | 'channel' | 'resolve';
  readonly progressMilli: number;
  readonly interruptMilli: number;
  readonly innerMu: number;
  readonly outerMu: number;
  /** Esta nave está envolvida. A resposta sobre MIM, e nunca a lista. */
  readonly involved: boolean;
}

export interface BossDrawState {
  readonly id: string;
  readonly generation: number;
  readonly mapIndex: number;
  readonly role: 'melee' | 'caster';
  readonly scaleBps: number;
  readonly x: number;
  readonly y: number;
  readonly dirX: number;
  readonly dirY: number;
  readonly radiusMu: number;
  readonly healthMilli: string;
  readonly maxHealthMilli: string;
  readonly poisoned: boolean;
  readonly bleeding: boolean;
  /** As penalidades de uma carta. Desenhadas pela família de sinais, não por esta. */
  readonly cards: OpenWorldCreatureCards;
  readonly ultimate: BossDrawUltimate | null;
}

export interface BossDrawEvent {
  readonly id: string;
  readonly ordinal: number;
  readonly kind:
    | 'hit'
    | 'contact'
    | 'cast'
    | 'impact'
    | 'death'
    | 'spawn'
    | 'restored'
    /** Uma etapa de um golpe físico. O corpo pisca, como pisca num contacto. */
    | 'strike'
    /** Uma ultimate começou a anunciar-se (gate V28I.BG). */
    | 'ultimate'
    /** Ela executou-se: quem falhou a mecânica morreu. */
    | 'ultimate_executed'
    /** Ela foi interrompida: os jogadores cumpriram a mecânica. */
    | 'ultimate_interrupted';
  readonly x: number;
  readonly y: number;
  /**
   * **O raio MECÂNICO da área**, quando o evento teve uma, já em unidades de
   * desenho. Ausente é "não teve área", e nesse caso o efeito é o de sempre.
   *
   * Uma etapa com área e o rebentamento de um corpo trazem-no. Um golpe num
   * casco não traz nenhum, e continua com o efeito curto de sempre.
   */
  readonly radius?: number;
  /**
   * **`false` tira o anel genérico de uma etapa** (gate V28I.CF.1A). Só o
   * Bombardeio de Profundidade o usa: os quatro círculos dele já dizem onde está
   * o perigo. Ausente é o de sempre, com anel.
   */
  readonly ring?: boolean;
}

interface Mote {
  readonly image: Phaser.GameObjects.Image;
  bornMs: number;
  lifeMs: number;
  vx: number;
  vy: number;
  fromScale: number;
  toScale: number;
}

interface BossView {
  readonly body: Phaser.GameObjects.Sprite;
  readonly ring: Phaser.GameObjects.Image;
  readonly bar: Phaser.GameObjects.Graphics;
  /** A chave com que o corpo está desenhado AGORA. Ver a vista de um mob. */
  bodyKey: string;
  drawX: number;
  drawY: number;
  flashUntilMs: number;
}

interface ShardGraphics {
  fillStyle(color: number, alpha?: number): ShardGraphics;
  fillCircle(x: number, y: number, radius: number): ShardGraphics;
  lineStyle(width: number, color: number, alpha?: number): ShardGraphics;
  strokeCircle(x: number, y: number, radius: number): ShardGraphics;
  generateTexture(key: string, width?: number, height?: number): unknown;
  destroy(): void;
}

interface TextureScene {
  readonly textures: { exists(key: string): boolean };
  readonly make: {
    graphics(config: { x: number; y: number }, addToScene: boolean): ShardGraphics;
  };
}

function makeShardTexture(scene: TextureScene): void {
  if (scene.textures.exists(TEXTURE.shard)) {
    return;
  }
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  graphics.fillStyle(0xffffff, 1);
  graphics.fillCircle(SHARD_PX / 2, SHARD_PX / 2, SHARD_PX / 2 - 1);
  graphics.generateTexture(TEXTURE.shard, SHARD_PX, SHARD_PX);
  graphics.destroy();
}

/** O disco que segura o lugar do corpo até a folha do mapa chegar. */
function makeBodyPlaceholderTexture(scene: TextureScene): void {
  if (scene.textures.exists(TEXTURE.body)) {
    return;
  }
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  graphics.fillStyle(0x2a3a4a, 1);
  graphics.fillCircle(BODY_PX / 2, BODY_PX / 2, BODY_PX / 2 - 2);
  graphics.generateTexture(TEXTURE.body, BODY_PX, BODY_PX);
  graphics.destroy();
}

function makeRingTexture(scene: TextureScene): void {
  if (scene.textures.exists(TEXTURE.ring)) {
    return;
  }
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  graphics.lineStyle(4, 0xffffff, 1);
  graphics.strokeCircle(RING_PX / 2, RING_PX / 2, RING_PX / 2 - 4);
  graphics.generateTexture(TEXTURE.ring, RING_PX, RING_PX);
  graphics.destroy();
}

export class OpenWorldBossArt {
  private readonly views = new Map<string, BossView>();
  private readonly shards: Mote[] = [];
  private readonly rings: Mote[] = [];
  private readonly seenEvent = new Map<string, number>();
  /** O contador da dispersão decorativa. Determinístico, e só isso decide. */
  private scatter = 0;
  constructor(
    private readonly scene: Phaser.Scene,
    private readonly depth: number,
  ) {
    const target = scene as unknown as TextureScene;
    makeShardTexture(target);
    makeRingTexture(target);
    makeBodyPlaceholderTexture(target);
  }

  update(
    bosses: readonly BossDrawState[],
    events: readonly BossDrawEvent[],
    nowMs: number,
    worldUnits: (mu: number) => number,
  ): void {
    const alive = new Set<string>();

    for (const boss of bosses) {
      alive.add(boss.id);
      const targetX = worldUnits(boss.x);
      const targetY = worldUnits(boss.y);
      const view = this.viewFor(boss.id, targetX, targetY);
      view.drawX += (targetX - view.drawX) * 0.3;
      view.drawY += (targetY - view.drawY) * 0.3;

      const radius = worldUnits(boss.radiusMu);
      const body = view.body;
      /*
       * **A arte do mapa dele, e nunca a de outro** (gate V28I.BI).
       *
       * A chave sai do `mapIndex` autoritativo — o mesmo que decide quem este
       * boss é. Enquanto a folha não existir, o corpo fica no marcador de lugar;
       * assim que ela existir, ele troca e a animação daquele mapa começa. Uma
       * travessia ou uma reconexão destrói as vistas e refaz este caminho, com a
       * folha já em cache.
       */
      const bodyKey = openWorldMapBodyTextureKey('boss', boss.mapIndex);
      if (view.bodyKey !== bodyKey && this.scene.textures.exists(bodyKey)) {
        body.setTexture(bodyKey);
        view.bodyKey = bodyKey;
        const animKey = openWorldMapBodyAnimKey('boss', boss.mapIndex);
        if (this.scene.anims.exists(animKey)) {
          body.play(animKey);
        }
      }
      body.setPosition(view.drawX, view.drawY);
      body.setDisplaySize(radius * 2.2, radius * 2.2);
      body.setRotation(Math.atan2(boss.dirY, boss.dirX) + Math.PI / 2);
      if (nowMs < view.flashUntilMs) {
        body.setTint(HIT_TINT);
      } else if (boss.bleeding) {
        body.setTint(BLEED_TINT);
      } else if (boss.poisoned) {
        body.setTint(POISON_TINT);
      } else {
        body.clearTint();
      }

      /* O indicador do papel: um anel fino, e o PNG fica intacto. */
      view.ring
        .setPosition(view.drawX, view.drawY)
        .setDisplaySize(radius * 2.5, radius * 2.5)
        .setTint(ROLE_TINT[boss.role])
        /*
         * **O anel acende durante uma ultimate.** Ela prende o corpo no sítio, e
         * o corpo tem de o dizer: um boss imóvel sem sinal nenhum leria-se como
         * um boss parado.
         */
        .setAlpha(boss.ultimate === null ? 0.28 : 0.55);

      this.drawBar(view, boss, radius);
    }

    for (const [key, view] of this.views) {
      if (alive.has(key)) {
        continue;
      }
      view.body.destroy();
      view.ring.destroy();
      view.bar.destroy();
      this.views.delete(key);
      this.seenEvent.delete(key);
    }

    for (const event of events) {
      const seen = this.seenEvent.get(event.id) ?? 0;
      if (event.ordinal <= seen) {
        continue;
      }
      this.seenEvent.set(event.id, event.ordinal);
      this.playEvent(event, nowMs, worldUnits);
    }

    this.stepMotes(this.shards, nowMs);
    this.stepMotes(this.rings, nowMs);
  }

  /**
   * A barra de vida: vermelha, e mais grossa do que a de um alienígena.
   *
   * **Nunca cortada pelo LOD**: uma barra que desaparecesse numa vista cheia
   * esconderia exactamente o facto de que um boss está quase a cair.
   */
  private drawBar(view: BossView, boss: BossDrawState, radius: number): void {
    const max = BigInt(boss.maxHealthMilli);
    const current = BigInt(boss.healthMilli);
    const fraction = max <= 0n ? 0 : Number((current * 1000n) / max) / 1000;
    const width = radius * 2.4;
    view.bar.clear();
    view.bar.setPosition(view.drawX, view.drawY);
    view.bar.fillStyle(0x1a0a0f, 0.8);
    view.bar.fillRect(-width / 2, -radius - 14, width, 6);
    view.bar.fillStyle(0xff4d5e, 1);
    view.bar.fillRect(-width / 2, -radius - 14, width * Math.max(0, Math.min(1, fraction)), 6);
  }

  private playEvent(event: BossDrawEvent, nowMs: number, worldUnits: (mu: number) => number): void {
    const x = worldUnits(event.x);
    const y = worldUnits(event.y);
    const view = this.views.get(event.id);
    switch (event.kind) {
      case 'hit': {
        if (view !== undefined) {
          view.flashUntilMs = nowMs + 90;
        }
        for (let index = 0; index < 3; index += 1) {
          this.shard(x, y, nowMs, HIT_TINT, SHARD_LIFE_MS);
        }
        break;
      }
      case 'contact': {
        this.ring(x, y, nowMs, ROLE_TINT.melee, 0.4, 1.5, RING_LIFE_MS);
        break;
      }
      case 'strike': {
        /*
         * **Uma etapa de um golpe físico aterrou** (gate V28I.BA).
         *
         * O anel abre até ao raio AUTORITATIVO da etapa quando ele veio — e
         * ele vem sempre nestes seis — portanto o que se vê é exatamente o que
         * feriu. A forma inteira é desenhada por `OpenWorldBossStrikeFx`; isto
         * é o impacto, e nada mais.
         */
        if (event.ring !== false) {
          const scale = event.radius === undefined ? 1.5 : event.radius / 24;
          this.ring(x, y, nowMs, ROLE_TINT.melee, 0.5, Math.max(1.5, scale), RING_LIFE_MS);
        }
        for (let index = 0; index < 4; index += 1) {
          this.shard(x, y, nowMs, ROLE_TINT.melee, SHARD_LIFE_MS);
        }
        break;
      }
      case 'cast': {
        this.ring(x, y, nowMs, ROLE_TINT.caster, 0.5, 1.8, RING_LIFE_MS);
        break;
      }
      case 'impact': {
        /*
         * **O impacto de um corpo**: um anel que abre até onde ele feriu, e
         * estilhaços.
         *
         * O raio é o AUTORITATIVO quando ele existe (gate V28I.Z.1): a
         * explosão abre exatamente até onde chegou o dano, e não até um número
         * escrito aqui. Um golpe sem área não traz raio nenhum e fica com o
         * núcleo curto de sempre.
         *
         * Desenhado por esta família e **não pela da carta** (gate V28I.BG): a
         * habilidade que reutilizava a Esfera Ígnea do jogador foi removida, e
         * um boss não desenha uma carta de ninguém.
         */
        const reach = event.radius ?? 60;
        this.ring(x, y, nowMs, HIT_TINT, 0.4, Math.max(1.2, reach / 42), RING_LIFE_MS);
        for (let index = 0; index < 6; index += 1) {
          this.shard(x, y, nowMs, HIT_TINT, SHARD_LIFE_MS);
        }
        break;
      }
      case 'ultimate': {
        /*
         * **O anúncio.** Um anel largo e frio, e nada mais: o que ele diz é
         * "isto começou", e a geometria da saída é do sistema das habilidades.
         */
        this.ring(x, y, nowMs, ULTIMATE_WARN_TINT, 0.9, 6.5, DEATH_LIFE_MS);
        break;
      }
      case 'ultimate_executed': {
        /* **Executou-se.** Branco, forte, e inconfundível com a morte do boss. */
        this.ring(x, y, nowMs, ULTIMATE_KILL_TINT, 0.8, 7.5, DEATH_LIFE_MS);
        for (let index = 0; index < 24; index += 1) {
          this.shard(x, y, nowMs, ULTIMATE_KILL_TINT, DEATH_LIFE_MS);
        }
        break;
      }
      case 'ultimate_interrupted': {
        /* **Foi cumprida.** Curto e verde: a recompensa de ter feito o trabalho. */
        this.ring(x, y, nowMs, ULTIMATE_BREAK_TINT, 0.5, 3.2, RING_LIFE_MS);
        for (let index = 0; index < 10; index += 1) {
          this.shard(x, y, nowMs, ULTIMATE_BREAK_TINT, SHARD_LIFE_MS);
        }
        break;
      }
      case 'death': {
        /* **Forte, curto e inequívoco.** Nada fica depois de ele desaparecer. */
        this.ring(x, y, nowMs, DEATH_TINT, 0.6, 4.5, DEATH_LIFE_MS);
        for (let index = 0; index < 18; index += 1) {
          this.shard(x, y, nowMs, DEATH_TINT, DEATH_LIFE_MS);
        }
        break;
      }
      case 'spawn': {
        /* Um anel que FECHA. Discreto, e sem sugerir recompensa nenhuma. */
        this.ring(x, y, nowMs, RESTORE_TINT, 3, 0.6, RING_LIFE_MS);
        break;
      }
      case 'restored': {
        /*
         * **A recuperação não é uma morte.** Um brilho frio e nada mais — sem
         * estilhaços, sem explosão, e sem nada que se leia como um fim.
         */
        this.ring(x, y, nowMs, RESTORE_TINT, 2.2, 0.8, RING_LIFE_MS);
        break;
      }
      default: {
        break;
      }
    }
  }

  private shard(x: number, y: number, nowMs: number, tint: number, lifeMs: number): void {
    /* **O corte do LOD acontece aqui, e só aqui**: resíduos, e nada mais. */
    if (this.shards.length >= SHARD_MAX) {
      return;
    }
    /*
     * **A dispersão é determinística.** Uma dobra sobre o contador de
     * estilhaços: dois clientes a ver a mesma morte veem o mesmo padrão, e o
     * desenho deixa de depender de um gerador que ninguém consegue reproduzir.
     */
    this.scatter = (this.scatter + 1) >>> 0;
    const folded = Math.imul(this.scatter ^ (this.scatter >>> 15), 0x2545_f491) >>> 0;
    const angle = ((folded % 3_600) / 3_600) * Math.PI * 2;
    const speed = 0.08 + (((folded >>> 12) % 100) / 100) * 0.12;
    const image = this.scene.add
      .image(x, y, TEXTURE.shard)
      .setDepth(this.depth + 3)
      .setTint(tint)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.shards.push({
      image,
      bornMs: nowMs,
      lifeMs,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      fromScale: 1.2,
      toScale: 0.2,
    });
  }

  private ring(
    x: number,
    y: number,
    nowMs: number,
    tint: number,
    fromScale: number,
    toScale: number,
    lifeMs: number,
  ): void {
    if (this.rings.length >= RING_MAX) {
      return;
    }
    const image = this.scene.add
      .image(x, y, TEXTURE.ring)
      .setDepth(this.depth + 3)
      .setTint(tint)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.rings.push({ image, bornMs: nowMs, lifeMs, vx: 0, vy: 0, fromScale, toScale });
  }

  private stepMotes(motes: Mote[], nowMs: number): void {
    for (let index = motes.length - 1; index >= 0; index -= 1) {
      const mote = motes[index];
      if (mote === undefined) {
        continue;
      }
      const age = nowMs - mote.bornMs;
      if (age >= mote.lifeMs) {
        mote.image.destroy();
        motes.splice(index, 1);
        continue;
      }
      const t = age / mote.lifeMs;
      mote.image.setPosition(mote.image.x + mote.vx * 16, mote.image.y + mote.vy * 16);
      mote.image.setScale(mote.fromScale + (mote.toScale - mote.fromScale) * t);
      mote.image.setAlpha(1 - t);
    }
  }

  /** A vista de um boss, criada ONDE ele está. Nunca a deslizar da origem. */
  private viewFor(key: string, atX: number, atY: number): BossView {
    const existing = this.views.get(key);
    if (existing !== undefined) {
      return existing;
    }
    /* Nasce no marcador de lugar, SEMPRE. Ver a vista de um mob. */
    const body = this.scene.add.sprite(atX, atY, TEXTURE.body).setDepth(this.depth);
    const ring = this.scene.add
      .image(atX, atY, TEXTURE.ring)
      .setDepth(this.depth - 1)
      .setBlendMode(Phaser.BlendModes.ADD);
    const bar = this.scene.add.graphics().setDepth(this.depth + 1);
    const view: BossView = {
      body,
      ring,
      bar,
      bodyKey: TEXTURE.body,
      drawX: atX,
      drawY: atY,
      flashUntilMs: 0,
    };
    this.views.set(key, view);
    return view;
  }

  destroy(): void {
    for (const view of this.views.values()) {
      view.body.destroy();
      view.ring.destroy();
      view.bar.destroy();
    }
    this.views.clear();
    this.seenEvent.clear();
    for (const mote of [...this.shards, ...this.rings]) {
      mote.image.destroy();
    }
    this.shards.length = 0;
    this.rings.length = 0;
  }
}
